import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  makeStyles,
  useTheme,
  Theme,
  Box,
  Button,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Paper,
} from '@material-ui/core';
import { useAsync } from 'react-use';
import { Entity } from '@backstage/catalog-model';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CloseIcon from '@material-ui/icons/Close';

import { CatalogSearchResultListItem } from '@backstage/plugin-catalog';
import {
  catalogApiRef,
  entityRouteParams,
  entityRouteRef,
  useEntityPresentation,
  CATALOG_FILTER_EXISTS,
} from '@backstage/plugin-catalog-react';
import { CatalogEntityDocument } from '@backstage/plugin-catalog-common';
import { TechDocsSearchResultListItem } from '@backstage/plugin-techdocs';
import { AdrSearchResultListItem } from '@backstage-community/plugin-adr';

import { SearchType, searchTranslationRef } from '@backstage/plugin-search';
import {
  SearchBar,
  SearchFilter,
  SearchResult,
  SearchResultGroup,
  SearchPagination,
  searchReactTranslationRef,
  useSearch,
} from '@backstage/plugin-search-react';
import { useTranslationRef } from '@backstage/frontend-plugin-api';
import {
  CatalogIcon,
  Content,
  DocsIcon,
  EmptyState,
  Header,
  Page,
  useContent,
} from '@backstage/core-components';
import { useApi, useRouteRef } from '@backstage/core-plugin-api';

import { MIN_SEARCH_TERM_LENGTH } from '../../apis';

const useStyles = makeStyles((theme: Theme) => ({
  bar: {
    padding: theme.spacing(1, 0),
  },
  filters: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  filter: {
    '& + &': {
      marginTop: theme.spacing(2.5),
    },
  },
  modalTitle: {
    gap: theme.spacing(1),
    display: 'grid',
    alignItems: 'center',
    gridTemplateColumns: '1fr auto',
  },
  modalFullResults: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

/**
 * The icon of an entity kind, taken from the same presentation api that marks
 * entities everywhere else in the app - so a product carries the product icon
 * and a department is told apart from a team, instead of every result in the
 * list looking like the next one.
 *
 * The api wants an entity and the index holds a document, so the two fields
 * the icon is picked by are handed over as a stand-in entity; the name is
 * there only to make it a ref the api accepts.
 */
const EntityKindIcon = (props: {
  kind: string;
  type?: string;
  name: string;
  namespace?: string;
}) => {
  const { Icon } = useEntityPresentation({
    apiVersion: 'backstage.io/v1alpha1',
    kind: props.kind,
    metadata: {
      name: props.name,
      namespace: props.namespace,
    },
    spec: { type: props.type },
  });
  // A kind the presentation api has no icon for keeps the generic one this
  // list showed for everything until now.
  return Icon ? <Icon /> : <CatalogIcon />;
};

const CatalogResultIcon = (props: { result: CatalogEntityDocument }) => {
  const { kind, type, namespace, location, title } = props.result;
  return (
    <EntityKindIcon
      kind={kind}
      type={type}
      name={location.split('/').pop() || title}
      namespace={namespace}
    />
  );
};

/**
 * How a hit is rendered, in the flat list of the page and in the sections of
 * both the page and the search modal in the sidebar - a hit should not look
 * like a different thing depending on where it was found.
 */
export const searchResultItems = (
  <>
    <CatalogSearchResultListItem
      icon={result => (
        <CatalogResultIcon result={result as CatalogEntityDocument} />
      )}
    />
    <TechDocsSearchResultListItem icon={<DocsIcon />} />
    <AdrSearchResultListItem icon={<DocsIcon />} />
  </>
);

/**
 * The stock "no results" of the search plugin, word for word - a flat result
 * list shows it on its own, the sections have to be given it.
 */
const NoResults = () => {
  const { t } = useTranslationRef(searchReactTranslationRef);
  return <EmptyState missing="data" title={t('noResultsDescription')} />;
};

/**
 * One section of the overview: the hits of a single kind - or of one type of a
 * kind, for groups - or the docs.
 */
type ResultSection = {
  title: string;
  icon: JSX.Element;
  type: string;
  filters?: Record<string, string>;
};

const catalogSection = (
  kind: string,
  title: string,
  entityType?: string,
): ResultSection => ({
  title: title,
  icon: (
    <EntityKindIcon
      kind={kind}
      type={entityType}
      name={kind.toLocaleLowerCase('en-US')}
    />
  ),
  type: 'software-catalog',
  filters: entityType ? { kind: kind, type: entityType } : { kind: kind },
});

/**
 * The sections of the overview, in the order they are shown - the kinds people
 * search for most first. Groups are split by type, the same types the "Type"
 * filter offers: a department and an AD group have little in common beyond
 * their kind.
 */
const resultSections: ResultSection[] = [
  catalogSection('Product', 'Products'),
  catalogSection('Group', 'Departments', 'department'),
  catalogSection('Group', 'Teams', 'team'),
  catalogSection('Group', 'AD Groups', 'ad-group'),
  catalogSection('Group', 'External Groups', 'external'),
  catalogSection('Component', 'Components'),
  catalogSection('System', 'Systems'),
  catalogSection('Resource', 'Resources'),
  catalogSection('API', 'APIs'),
  catalogSection('Provider', 'Providers'),
  catalogSection('Template', 'Templates'),
  catalogSection('User', 'Users'),
  { title: 'Documentation', icon: <DocsIcon />, type: 'techdocs' },
  { title: 'ADRs', icon: <DocsIcon />, type: 'adr' },
];

/**
 * How many hits a section shows before "See all" has to be followed.
 */
const SECTION_PAGE_LIMIT = 5;

/**
 * Stands in for the "no results" of a section, which is only rendered once the
 * section has come back empty - so being mounted is what says it is empty, and
 * being unmounted again (a new term loading, hits arriving) says it no longer
 * is. Renders nothing; the section hides itself as before.
 */
const EmptySection = (props: { onEmpty: (empty: boolean) => void }) => {
  const { onEmpty } = props;
  useEffect(() => {
    onEmpty(true);
    return () => onEmpty(false);
  }, [onEmpty]);
  return null;
};

/**
 * The hits split into one section per kind, each asked for with a query of its
 * own, so a section is not limited to what happened to land on one mixed page.
 * "See all" of a section leads to the flat list filtered to that kind.
 *
 * `SearchResultGroup` queries again whenever its query object changes, so the
 * queries are memoized on what they are built from - otherwise every render of
 * the page would fire a round of requests.
 *
 * `onClick` fires for a click anywhere in a section, a hit or "See all" - the
 * modal closes itself with it.
 *
 * An empty section hides itself, so when all of them are empty the stock "no
 * results" is shown in their place rather than nothing at all.
 */
const ResultSections = (props: { onClick?: () => void }) => {
  const { term, filters } = useSearch();
  const [emptySections, setEmptySections] = useState<Record<string, boolean>>(
    {},
  );

  // One callback per section that stays the same across renders, or every
  // render would remount the effect of `EmptySection` and flip its report.
  const onEmpty = useMemo(
    () =>
      Object.fromEntries(
        resultSections.map(section => [
          section.title,
          (empty: boolean) =>
            setEmptySections(previous => ({
              ...previous,
              [section.title]: empty,
            })),
        ]),
      ),
    [],
  );
  const allEmpty = resultSections.every(
    section => emptySections[section.title],
  );

  const queries = useMemo(() => {
    // The "Type" filter belongs to the flat list of groups; left over from
    // there it would narrow every section down to, say, teams.
    const { type: _type, ...sharedFilters } = filters;
    return resultSections.map(section => ({
      term: term,
      types: [section.type],
      // The catalog filters on the left make no sense to the docs index.
      filters: section.filters ? { ...sharedFilters, ...section.filters } : {},
      pageLimit: SECTION_PAGE_LIMIT,
    }));
  }, [term, filters]);

  // Every section hides itself when empty, and the gate answers a short term
  // with nothing - so without this the page would be blank.
  if (term.trim().length < MIN_SEARCH_TERM_LENGTH) {
    return <NoResults />;
  }

  return (
    <>
      {allEmpty && <NoResults />}
      {resultSections.map((section, index) => (
        <SearchResultGroup
          key={section.title}
          query={queries[index]}
          icon={section.icon}
          title={section.title}
          noResultsComponent={<EmptySection onEmpty={onEmpty[section.title]} />}
          onClick={props.onClick}
        >
          {searchResultItems}
        </SearchResultGroup>
      ))}
    </>
  );
};

/** The annotation the CMDB provider stamps the vuid of an entity onto. */
const VUID_ANNOTATION = 'schema.dp.netrtl.com/vuid';

/**
 * The CMDB entity whose vuid the term is, if there is one. A pasted vuid
 * (`dept-tee1c`) is meant to find that one entity, but the full text search
 * splits it at the hyphen and fuzzes the parts - locally, on Lunr, `dept`
 * alone matches every department - so the exact match is looked up in the
 * catalog on its own, by name, the same way on every search engine.
 *
 * Only a single word is looked up, a vuid has no spaces; and only entities
 * that carry the vuid annotation count, so a component that happens to be
 * named like the term is not taken for a vuid.
 */
function useExactVuidMatch(term: string): Entity | undefined {
  const catalogApi = useApi(catalogApiRef);
  const candidate = term.trim();
  const { value } = useAsync(async () => {
    if (candidate.length < MIN_SEARCH_TERM_LENGTH || /\s/.test(candidate)) {
      return undefined;
    }
    const { items } = await catalogApi.getEntities({
      filter: {
        'metadata.name': candidate,
        [`metadata.annotations.${VUID_ANNOTATION}`]: CATALOG_FILTER_EXISTS,
      },
      fields: [
        'apiVersion',
        'kind',
        'metadata.name',
        'metadata.namespace',
        'metadata.title',
        'spec.type',
      ],
      limit: 1,
    });
    return items[0];
  }, [catalogApi, candidate]);
  // `useAsync` keeps the last value while the next term is looked up, which
  // would show the match of a term that is no longer in the box.
  return value?.metadata.name.toLocaleLowerCase('en-US') ===
    candidate.toLocaleLowerCase('en-US')
    ? value
    : undefined;
}

/** Goes to the page of an entity, as a hit of the search does. */
function useOpenEntity(): (entity: Entity) => void {
  const navigate = useNavigate();
  const entityRoute = useRouteRef(entityRouteRef);
  return useCallback(
    (entity: Entity) => navigate(entityRoute(entityRouteParams(entity))),
    [navigate, entityRoute],
  );
}

/**
 * The exact vuid match, on top of the results of the search modal and of the
 * search page.
 */
const ExactMatch = (props: { entity: Entity; onClick: () => void }) => {
  const { entity, onClick } = props;
  const { primaryTitle } = useEntityPresentation(entity);
  return (
    <List
      subheader={
        <ListSubheader disableSticky disableGutters>
          Exact match
        </ListSubheader>
      }
    >
      <ListItem button onClick={onClick}>
        <ListItemIcon>
          <EntityKindIcon
            kind={entity.kind}
            type={entity.spec?.type as string | undefined}
            name={entity.metadata.name}
            namespace={entity.metadata.namespace}
          />
        </ListItemIcon>
        <ListItemText primary={primaryTitle} secondary="Press Enter to open" />
      </ListItem>
    </List>
  );
};

/**
 * What the search modal in the sidebar shows instead of its stock content: the
 * same sections as the page, so a hit is found under the same heading in both.
 * Search bar, close button and "View full results" are kept as upstream has
 * them; the pager goes, a section has "See all" instead.
 *
 * A term that is the vuid of an entity puts that entity on top as the exact
 * match, and Enter opens it instead of the full results - "View full results"
 * still leads there.
 */
export const SearchModalContent = (props: { toggleModal: () => void }) => {
  const { toggleModal } = props;
  const classes = useStyles();
  const navigate = useNavigate();
  const { transitions } = useTheme();
  const { focusContent } = useContent();
  const { term } = useSearch();
  const { t } = useTranslationRef(searchTranslationRef);
  const searchBarRef = useRef<HTMLInputElement | null>(null);

  // The modal is opened to type into it.
  useEffect(() => {
    searchBarRef.current?.focus();
  }, []);

  // Upstream hands the focus back to the page once the modal has faded out.
  const close = useCallback(() => {
    toggleModal();
    setTimeout(focusContent, transitions.duration.leavingScreen);
  }, [toggleModal, focusContent, transitions]);

  // Closed before navigating, never after: the modal hides itself as soon as
  // the location changes, and the router renders that change right away - a
  // `close` after it would toggle the modal open again.
  const showFullResults = useCallback(() => {
    close();
    navigate(`/search?query=${encodeURIComponent(term)}`);
  }, [navigate, term, close]);

  const exactMatch = useExactVuidMatch(term);
  const openEntity = useOpenEntity();
  const openExactMatch = useCallback(() => {
    if (exactMatch) {
      close();
      openEntity(exactMatch);
    }
  }, [exactMatch, openEntity, close]);

  return (
    <>
      <DialogTitle>
        <Box className={classes.modalTitle}>
          {/* Enter closes the modal while its keydown is still being handled,
              and the modal hands the focus back to the sidebar button it was
              opened with; the button would take the rest of the key stroke as
              a click and open the modal again. */}
          <SearchBar
            inputProps={{ ref: searchBarRef }}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault();
              }
            }}
            onSubmit={exactMatch ? openExactMatch : showFullResults}
          />
          <IconButton aria-label="close" onClick={toggleModal}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box className={classes.modalFullResults}>
          <Button
            color="primary"
            endIcon={<ArrowForwardIcon />}
            onClick={showFullResults}
          >
            {t('searchModal.viewFullResults')}
          </Button>
        </Box>
        <Divider />
        {exactMatch && (
          <>
            <ExactMatch entity={exactMatch} onClick={openExactMatch} />
            <Divider />
          </>
        )}
        <ResultSections onClick={close} />
      </DialogContent>
    </>
  );
};

const SearchPage = () => {
  const classes = useStyles();
  const { term, types, filters } = useSearch();
  const catalogApi = useApi(catalogApiRef);
  // The overview is the page's starting point; picking a kind, the docs or the
  // ADRs narrows it down to the flat list with its pagination.
  const showSections =
    !filters.kind && !types.includes('techdocs') && !types.includes('adr');

  // As in the modal: a vuid puts its entity on top, and Enter opens it. Enter
  // does nothing on this page otherwise, the results follow the typing.
  const exactMatch = useExactVuidMatch(term);
  const openEntity = useOpenEntity();
  const openExactMatch = useCallback(() => {
    if (exactMatch) {
      openEntity(exactMatch);
    }
  }, [exactMatch, openEntity]);

  return (
    <Page themeId="home">
      <Header title="Search" />
      <Content>
        <Grid container direction="row">
          <Grid item xs={12}>
            <Paper className={classes.bar}>
              <SearchBar onSubmit={exactMatch ? openExactMatch : undefined} />
            </Paper>
          </Grid>
          <Grid item xs={3}>
            <SearchType.Accordion
              name="Result Type"
              defaultValue="software-catalog"
              types={[
                {
                  value: 'software-catalog',
                  name: 'Software Catalog',
                  icon: <CatalogIcon />,
                },
                {
                  value: 'techdocs',
                  name: 'Documentation',
                  icon: <DocsIcon />,
                },
                {
                  value: 'adr',
                  name: 'Architecture Decision Records',
                  icon: <DocsIcon />,
                },
              ]}
            />
            <Paper className={classes.filters}>
              {types.includes('techdocs') && (
                <SearchFilter.Select
                  className={classes.filter}
                  label="Entity"
                  name="name"
                  values={async () => {
                    // Return a list of entities which are documented.
                    const { items } = await catalogApi.getEntities({
                      fields: ['metadata.name'],
                      filter: {
                        'metadata.annotations.backstage.io/techdocs-ref':
                          CATALOG_FILTER_EXISTS,
                      },
                    });

                    const names = items.map(entity => entity.metadata.name);
                    names.sort();
                    return names;
                  }}
                />
              )}
              <SearchFilter.Select
                className={classes.filter}
                label="Kind"
                name="kind"
                values={[
                  'System',
                  'Component',
                  'Product',
                  'Resource',
                  'Provider',
                  'API',
                  'Template',
                  'Group',
                  'User',
                ]}
              />
              {filters.kind === 'Group' && (
                <SearchFilter.Select
                  className={classes.filter}
                  label="Type"
                  name="type"
                  values={['department', 'team', 'ad-group', 'external']}
                />
              )}
              <SearchFilter.Checkbox
                className={classes.filter}
                label="Lifecycle"
                name="lifecycle"
                values={['experimental', 'production']}
              />
            </Paper>
          </Grid>
          <Grid item xs={9}>
            {exactMatch && (
              <ExactMatch entity={exactMatch} onClick={openExactMatch} />
            )}
            {showSections ? (
              <ResultSections />
            ) : (
              <>
                <SearchPagination />
                <SearchResult>{searchResultItems}</SearchResult>
              </>
            )}
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
};

export const searchPage = <SearchPage />;
